import each from 'lodash/each';
import _get from 'lodash/get';
import isArray from 'lodash/isArray';
import _debounce from 'lodash/debounce';
import MobileDetect from 'mobile-detect';
import * as Sentry from '@sentry/browser';

import config from 'config';
import requestService from './requestService';
import hateoas from './hateoas';
import storage from './storage';

const jwtSuffix = config.ENVIRONMENT === 'Production' ? '' : '-dev';
const md = new MobileDetect(window.navigator.userAgent);

export const get = ({ dispatch }, url) => {
  return dispatch('async', () => requestService.get(url));
};

export const externalGet = ({ dispatch }, url) => {
  return requestService.get(url, false);
};

export const backgroundGet = ({ dispatch }, url) => {
  return dispatch('async', { promise: () => requestService.get(url), backgroundRequest: true });
};

export const put = ({ dispatch }, { apiUrl, payload }) => {
  return dispatch('async', () => requestService.put(apiUrl, payload));
};

export const del = ({ dispatch }, url) => {
  return dispatch('async', () => requestService.delete(url));
};

export const post = ({ dispatch }, { apiUrl, payload }) => {
  return dispatch('async', () => requestService.post(apiUrl, payload));
};

export const postFormData = ({ dispatch }, { apiUrl, formData }) => {
  return dispatch('async', () => requestService.postFormData(apiUrl, formData));
};
export const patch = ({ dispatch }, { apiUrl, payload, nonPublicScope = false }) => {
  return dispatch('async', () => requestService.patch(apiUrl, payload, true, nonPublicScope));
};

export const getApiRoutes = async ({ commit, dispatch }) => {
  let response;

  if (!Object.keys(hateoas.routes).length) {
    response = await dispatch('async', () => requestService.get(`${config.APP_PROTOCOL}:${config.API_URL}${config.API_ROOT}`, false));
    hateoas.routes = response.routes;
  }

  return response;
};

export const logError = async ({ commit }, error) => {
  if (!md.is('bot') && !window.location.search.includes('_escaped_fragment_') && navigator.userAgent.indexOf('HeadlessChrome') === -1) {
    try {
      let response,
        isViralstyleUrl = error.url && error.url.includes('viralstyle'),
        isCopyrightError = error.status === 410 && isViralstyleUrl;

      if (error.type === 'card_error') {
        commit('addNotification', error.message);

        response = Promise.resolve();
      } else if (error.status !== 404) {
        if (typeof error.json === 'function') {
          response = await error.json();

          if (_get(response, 'data.token')) {
            storage.setDomainCookie(`client-jwt${jwtSuffix}`, response.data.token, config.APP_DOMAIN);
          } else if (_get(response, 'data.jwt')) {
            storage.setDomainCookie(`client-jwt${jwtSuffix}`, response.data.jwt, config.APP_DOMAIN);
          } else if (response.jwt) {
            storage.setDomainCookie(`client-jwt${jwtSuffix}`, response.jwt, config.APP_DOMAIN);
          }

          each(response.errors, errors => {
            if (isArray(errors)) {
              each(errors, reason => commit('addNotification', reason));
            } else {
              commit('addNotification', errors);
            }
          });

          if (response.message && !response.errors) {
            commit('addNotification', response.message);
          }

          if (!isCopyrightError) {
            if (config.RAVEN_URL) {
              Sentry.withScope(scope => {
                scope.setExtra('response', JSON.stringify(response));

                Sentry.captureException(new Error(response.message));
              });
            } else {
              console.error(response)
            }
          }
        } else {
          response = error;

          if (!isCopyrightError) {
            if (config.RAVEN_URL) {
              Sentry.captureException(error);
            } else {
              console.error(error);
            }
          }
        }
      } else {
        response = Promise.resolve();
      }

      return response;
    } catch (err) {
      // swallow this error to prevent sentry from being polluted with error.json is not a function

      // log this error to the console incase its something other than json related... unlikely.
      console.error(err);

      return Promise.resolve();
    }
  } else {
    return Promise.resolve();
  }
};

export const sharedLogin = async ({ dispatch }, { apiUrl, email, password, token }) => {
  let payload = {
    email,
    password,
  };

  if (token) {
    payload.token = token;
  }

  let response = await dispatch('post', { apiUrl, payload });

  // until refactored designer is out this must remain... sigh...
  // if (response.data.user.is_legacy) {
  await dispatch('loginLegacy', payload);
  // }

  return response;
};

export const loginLegacy = ({ dispatch }, { email, password, token }) => {
  return dispatch('async', () => requestService.loginLegacy(email, password, token));
};

export const logoutLegacy = ({ dispatch }) => {
  return dispatch('async', () => requestService.logoutLegacy());
};

export const getVersion = ({ dispatch }) => {
  return dispatch('async', () => requestService.getVersion());
};

export const debounce = ({ state, dispatch, commit }, { action, args }) => {
  if (state.debounce) {
    state.debounce.cancel();
  }

  commit('updateState', { object: state, property: 'debouncing', value: true });

  let debounce = _debounce(async () => {
    if (state.previousDebounce) {
      await state.previousDebounce;
    }

    commit('updateState', { object: state, property: 'debouncing', value: false });

    let previousDebounce = dispatch(action, args);

    commit('updateState', { object: state, property: 'previousDebounce', value: previousDebounce });

    previousDebounce.finally(() => {
      commit('updateState', { object: state, property: 'previousDebounce', value: null });
    });
  }, 800);

  commit('updateState', { object: state, property: 'debounce', value: debounce });

  state.debounce();
};